<script lang="ts">
  import CheckIcon from '@lucide/svelte/icons/check'
  import ChevronsUpDownIcon from '@lucide/svelte/icons/chevrons-up-down'
  import { Badge } from '$lib/components/ui/badge/index.js'
  import * as Command from '$lib/components/ui/command/index.js'
  import * as Popover from '$lib/components/ui/popover/index.js'
  import { Button } from '$lib/components/ui/button/index.js'
  import { cn } from '$lib/utils.js'

  let {
    show,
    class: sclass,
    options,
    optionKind,
    closeAndFocusTrigger,
    handleSelection,
    values = $bindable([] as string[]),
  } = $props()

  let open = $state(false)
  let triggerRef = $state<HTMLButtonElement>(null!)

  const kindClass: Record<string, string> = {
    genre: 'text-latte-mauve dark:text-macchiato-mauve',
    status: 'text-latte-yellow dark:text-macchiato-yellow',
    year: 'text-latte-blue dark:text-macchiato-blue',
    format: 'text-latte-peach dark:text-macchiato-peach',
    tag: 'text-latte-sky dark:text-macchiato-sky',
  }

  const badgeClass: Record<string, string> = {
    genre: 'bg-latte-mauve/90 text-white dark:bg-macchiato-mauve dark:text-macchiato-base',
    status: 'bg-latte-yellow/90 text-white dark:bg-macchiato-yellow dark:text-macchiato-base',
    year: 'bg-latte-blue/90 text-white dark:bg-macchiato-blue dark:text-macchiato-base',
    format: 'bg-latte-peach/90 text-white dark:bg-macchiato-peach dark:text-macchiato-base',
    tag: 'bg-latte-sky/90 text-white dark:bg-macchiato-sky dark:text-macchiato-base',
  }

  const label =
    optionKind[optionKind.length - 1].toLowerCase() == 's' ? optionKind : `${optionKind}s`

  function toggle(option: string) {
    values = values.includes(option)
      ? values.filter((e) => e != option)
      : [...values, option]
    handleSelection(optionKind, values)
    closeAndFocusTrigger()
  }
</script>

<div
  class={cn(
    show ? 'flex' : 'hidden',
    'min-w-0 flex-col gap-0.5 rounded-md border border-latte-surface1/60 bg-latte-mantle/45 px-2.5 py-1.5 shadow-sm backdrop-blur-sm dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/45',
    sclass
  )}
>
  <span class={cn('text-[8px] leading-none font-light', kindClass[optionKind])}>
    {label}
  </span>
  <Popover.Root bind:open>
    <Popover.Trigger bind:ref={triggerRef}>
      {#snippet child({ props })}
        <Button
          {...props}
          variant="ghost"
          class="h-8 w-[200px] justify-start overflow-hidden relative px-0 text-latte-text/80 hover:bg-transparent hover:text-latte-mauve dark:text-macchiato-text/80 dark:hover:text-macchiato-mauve"
          role="combobox"
          aria-expanded={open}
        >
          {#if values.length > 0}
            {#each values.slice(0, 2) as g}
              <Badge
                class={cn(
                  'm-0.5 overflow-hidden rounded-sm py-0 font-medium',
                  badgeClass[optionKind]
                )}>{g}</Badge
              >
            {/each}
            {#if values.length > 2}
              <Badge
                class={cn(
                  'm-0.5 overflow-hidden rounded-sm py-0 font-medium',
                  badgeClass[optionKind]
                )}>+{values.length - 2}</Badge
              >
            {/if}
          {:else}
            <span class="text-sm leading-tight">Any</span>
          {/if}
          <ChevronsUpDownIcon class="absolute right-0 size-3.5 opacity-50" />
        </Button>
      {/snippet}
    </Popover.Trigger>
    <Popover.Content
      class="w-[200px] border-latte-surface1/60 bg-latte-mantle p-0 dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle"
    >
      <Command.Root>
        <Command.Input placeholder="Search {optionKind}..." />
        <Command.List
          class="scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-900"
        >
          <Command.Empty>no {optionKind} found.</Command.Empty>
          <Command.Group value="frameworks">
            {#each options as option (option)}
              <Command.Item value={option} onSelect={() => toggle(option)}>
                <CheckIcon class={cn(!values.includes(option) && 'text-transparent')} />
                {option}
              </Command.Item>
            {/each}
          </Command.Group>
        </Command.List>
      </Command.Root>
    </Popover.Content>
  </Popover.Root>
</div>
